import { useEffect, useState } from 'react';
import { ApiClient } from '../api/client';
import type { LeaderboardRow } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { displayName, emailVerified, realmRoles } from '../auth/claims';
import { config } from '../config';
import { useI18n } from '../i18n';

const api = new ApiClient({ baseUrl: config.apiBaseUrl });

/**
 * 「玩家的紀錄」。
 *
 * ⚠️ 契約缺口：C02 目前**沒有**玩家自助查詢自己對局紀錄的端點
 * （§7 的 `/admin/games/{g}/players/{subject_id}` 是遊戲管理員專用）。
 * 我們不自行發明端點（會直接 404），改成：
 *   1. 顯示 token / userinfo 裡可以證明的身分資料
 *   2. 用公開排行榜（C02 §2，契約明定）比對 display_name 當作暫時的紀錄來源
 *   3. 在畫面上**明說**這是暫代方案，並指向 handoff 文件
 * 已寫入 docs/ai/handoffs/2026-08-14-ws05a-frontend-contract-gaps.md 請後端補 `GET /me`。
 */
export function MePage() {
  const { t } = useI18n();
  const auth = useAuth();
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);

  const name = displayName(auth.user);

  useEffect(() => {
    if (!auth.user) return;
    const ac = new AbortController();
    api
      .getLeaderboard('rps', { window: 'all' }, ac.signal)
      .then((r) => setRows(r.items.filter((row) => row.display_name === name)))
      .catch(() => setRows([]));
    return () => ac.abort();
  }, [auth.user, name]);

  if (auth.loading) return <p className="muted">{t('common.loading')}</p>;

  if (!auth.user) {
    return (
      <div className="stack">
        <p className="muted">{t('auth.notLoggedIn')}</p>
        {auth.enabled ? (
          <button type="button" className="btn btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => auth.login('/me')}>
            {t('auth.login')}
          </button>
        ) : null}
      </div>
    );
  }

  const roles = realmRoles(auth.user);

  return (
    <div className="stack">
      <h1 className="section-title">{t('auth.me')}</h1>

      {!emailVerified(auth.user) ? <div className="notice notice--warn">{t('auth.emailUnverified')}</div> : null}

      <section className="card stack stack--tight">
        <h2 className="game-card__title">{t('auth.profile')}</h2>
        <div className="spread">
          <span className="faint">{t('game.player')}</span>
          <strong>{name}</strong>
        </div>
        <div className="spread">
          <span className="faint">{t('auth.email')}</span>
          <span>{auth.user.profile.email ?? '—'}</span>
        </div>
        <div className="spread">
          <span className="faint">{t('auth.subject')}</span>
          <code className="mono">{auth.user.profile.sub}</code>
        </div>
        <div className="spread">
          <span className="faint">{t('auth.roles')}</span>
          <span className="row">
            {roles.length > 0 ? roles.map((r) => <span className="badge" key={r}>{r}</span>) : <span>—</span>}
          </span>
        </div>
      </section>

      <section className="card stack stack--tight">
        <h2 className="game-card__title">{t('auth.record')}</h2>
        <p className="faint">{t('auth.record.pending')}</p>
        {rows === null ? (
          <p className="muted">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="muted">{t('auth.record.none')}</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('game.rank')}</th>
                  <th scope="col">{t('game.wins')}</th>
                  <th scope="col">{t('game.losses')}</th>
                  <th scope="col">{t('game.draws')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.rank}>
                    <td>{r.rank}</td>
                    <td>{r.wins}</td>
                    <td>{r.losses}</td>
                    <td>{r.draws}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
